/**
 * Dialog zu einer Aktion am Nachweis (ADR 007): fragt genau die Felder ab, die die Aktion setzt,
 * dazu den Kommentar für die Nachweishistorie.
 *
 * Welche Felder das sind und welche davon Pflicht, sagt der Server je Aktion
 * (`Evidence.aktionen`); hier steht nur, wie ein Feld aussieht. Leer gelassene Datumsfelder mit
 * Vorbelegung füllt der Server mit dem heutigen Datum – der Dialog zeigt es schon vorab an.
 * Ein Feld mit `vorbelegt_aus` läuft mit seinem Quellfeld mit („Ausgestellt am“ mit „Geliefert
 * am“), bis man es selbst ändert. Für den Gutachter („Angefragt bei“) schlägt das Feld die
 * bisherigen Gutachter des Mandanten vor.
 *
 * Der erste Schritt aus „Offen“ zeigt oben den Bearbeitungsweg: Wer ihn ändert, bekommt den
 * ersten Schritt des anderen Wegs mit dessen Feldern („das mache ich doch selbst“). „Bearbeitungsweg
 * ändern“ fragt den neuen Weg ab und warnt, wenn dabei Fortschritt verloren geht. „Anfordern von“
 * ist intern ein Benutzer des Mandanten, extern eine Institution mit E-Mail-Adresse; wählt man
 * eine bekannte Institution bzw. einen bekannten Gutachter, schlägt das Feld ihre zuletzt
 * verwendete E-Mail-Adresse vor.
 *
 * Fragt die Aktion einen Betrag der Kostenposition ab (`cost_field`: Plankosten beim
 * Beauftragen, Istkosten beim Freigeben), steht er unter den Feldern – nur mit Recht an den
 * Kosten, ohne Schreibrecht nur lesend. Vorbelegt ist er mit dem bestehenden Betrag, sonst mit
 * dem aus `prefill_from`; beim Freigeben stehen die Plankosten zum Vergleich darunter.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { addDays, format } from "date-fns";
import DialogTemplate from "../../../layout/DialogTemplate";
import { colors } from "../../../../styles/theme";
import Autocomplete from "../../../common/inputs/Autocomplete";
import Button from "../../../common/inputs/Button";
import Dropdown from "../../../common/inputs/Dropdown";
import InputField from "../../../common/inputs/InputField";
import InputFieldDate from "../../../common/inputs/InputFieldDate";
import InputFieldMultiline from "../../../common/inputs/InputFieldMultiline";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import DokumentListe from "../../../common/inputs/DokumentListe";
import {
  Dokument,
  Evidence,
  EvidenceAktion,
  EvidenceExpert,
  EvidenceRequestedFrom,
  EvidenceStatus,
  EvidenceUebergang,
  EvidenceUebergangFeldName,
  PermitCostItem,
  ProcessingPath,
} from "../../../../types";
import { formatCurrency } from "../../../../utils/formatters";
import {
  suggestedEmail,
  useEvidenceExperts,
} from "../hooks/useEvidenceExperts";
import { useUserOptions } from "../../overview/hooks/useUserOptions";
import { alsBetrag, betragAlsFeldwert } from "./betrag";
import { processingPathTooltip } from "./processingPathTooltip";

/** Was man an den Kosten darf: nichts, sehen oder ändern („Genehmigung › Kosten“) */
export type CostAccess = "none" | "read" | "write";

interface EvidenceUebergangDialogProps {
  evidence: Evidence | null;
  /** Die gewählte Aktion; `null` schließt den Dialog */
  aktion: EvidenceAktion | null;
  /** Nur „Bearbeitungsweg ändern“: der schon gewählte neue Weg */
  targetPath?: ProcessingPath;
  /** Titel und Bestätigungsknopf – derselbe Name wie am Knopf, der den Dialog geöffnet hat */
  label: string;
  onClose: () => void;
  /** Führt die Aktion aus; `false`, wenn der Server sie abgelehnt hat (Dialog bleibt offen) */
  onSubmit: (data: EvidenceUebergang) => Promise<boolean>;
  /** Hinweis über den Feldern – etwa welche Termine beim Archivieren mitgelöscht werden */
  notice?: string;
  /** Kostenposition des Nachweises, falls es schon eine gibt */
  costItem?: PermitCostItem;
  costAccess: CostAccess;
}

type Wert = string | Dokument[];
type Werte = Partial<Record<EvidenceUebergangFeldName, Wert>>;

/** „Anfordern von“, wie der Dialog es hält: die Seite und je Seite die Angaben */
interface Requester {
  side: "internal" | "external";
  userId: string;
  institution: string;
  email: string;
}

const REQUESTER_SIDES = [
  { value: "internal", label: "Intern" },
  { value: "external", label: "Extern" },
];

/** Vorbelegung: wer schon eingetragen ist, sonst eine Institution – der häufigere Fall */
const requesterOf = (evidence: Evidence): Requester => ({
  side: evidence.requested_from_user_id ? "internal" : "external",
  userId: evidence.requested_from_user_id ?? "",
  institution: evidence.institution ?? "",
  email: evidence.requested_from_email ?? "",
});

const requesterPayload = (a: Requester): EvidenceRequestedFrom =>
  a.side === "internal"
    ? { user_id: a.userId || null }
    : {
        institution: a.institution.trim() || null,
        email: a.email.trim() || null,
      };

const isRequesterEmpty = (a: Requester): boolean =>
  a.side === "internal" ? !a.userId : !a.institution.trim();

/**
 * Übernimmt beim Wählen eines bekannten Namens dessen letzte E-Mail-Adresse – solange man die
 * Adresse nicht selbst geändert hat, also sie leer ist oder noch die des vorigen Namens trägt.
 */
const emailForName = (
  suggestions: EvidenceExpert[],
  previousName: string,
  name: string,
  email: string,
): string => {
  const previous = suggestedEmail(suggestions, previousName) ?? "";
  if (email && email !== previous) return email;
  return suggestedEmail(suggestions, name) ?? (email === previous ? "" : email);
};

/** Ein Wert ohne Inhalt – ein Dokument zählt nur mit Link */
const istLeer = (wert: Wert | undefined): boolean =>
  Array.isArray(wert)
    ? wert.every((dokument) => !dokument.url.trim())
    : !wert?.trim();

/** Wie der Wert zum Server geht: Dokumente nur mit Link, Text getrimmt, leer als `null` */
const zumSenden = (wert: Wert | undefined) => {
  if (Array.isArray(wert)) {
    const dokumente = wert.filter((dokument) => dokument.url.trim());
    return dokumente.length > 0 ? dokumente : null;
  }
  return wert?.trim() || null;
};

const heutePlus = (tage: number): string =>
  format(addDays(new Date(), tage), "yyyy-MM-dd");

/** Wert eines Feldes am Nachweis, sofern er dort unter demselben Namen steht */
const storedValue = (
  evidence: Evidence,
  name: EvidenceUebergangFeldName,
): unknown => (evidence as unknown as Record<string, unknown>)[name];

/**
 * Vorbelegung: was am Nachweis schon steht, sonst das heutige Datum, wo der Server es setzt,
 * sonst der Vorschlag des Servers (z. B. Angebotsfrist in 14 Tagen). Felder mit `vorbelegt_aus`
 * kommen danach, weil ihr Quellfeld im Dialog erst feststehen muss.
 */
const startwerte = (evidence: Evidence, aktion: EvidenceAktion): Werte => {
  const werte: Werte = Object.fromEntries(
    aktion.felder.map((feld) => {
      if (feld.typ === "dokument_liste") {
        const bestand =
          feld.bestandswert_vorbelegen !== false
            ? ((storedValue(evidence, feld.name) as Dokument[] | undefined) ??
              [])
            : [];
        return [feld.name, bestand];
      }
      const bestand =
        feld.bestandswert_vorbelegen !== false
          ? (storedValue(evidence, feld.name) as string | null | undefined)
          : undefined;
      const vorschlag = feld.heute_vorbelegt
        ? heutePlus(0)
        : feld.vorschlag_in_tagen != null
          ? heutePlus(feld.vorschlag_in_tagen)
          : "";
      return [feld.name, bestand || vorschlag];
    }),
  );
  aktion.felder.forEach((feld) => {
    const quelle = feld.vorbelegt_aus;
    if (!quelle || werte[feld.name]) return;
    const wert =
      quelle in werte
        ? werte[quelle]
        : (storedValue(evidence, quelle) as string | undefined);
    if (typeof wert === "string") werte[feld.name] = wert;
  });
  return werte;
};

/** Der Betrag, mit dem das Kostenfeld startet: der bestehende, sonst der aus `prefill_from` */
const initialAmount = (
  aktion: EvidenceAktion,
  costItem: PermitCostItem | undefined,
): string => {
  const field = aktion.cost_field;
  if (!field) return "";
  const existing = costItem?.[field.name];
  const prefill = field.prefill_from
    ? costItem?.[field.prefill_from]
    : undefined;
  return betragAlsFeldwert(existing ?? prefill);
};

/**
 * Setzt ein Feld und lässt die Felder mitlaufen, die aus ihm vorbelegt sind – solange sie noch
 * den bisherigen Wert des Quellfelds tragen, also nicht selbst geändert wurden.
 */
const mitFolgefeldern = (
  aktion: EvidenceAktion,
  prev: Werte,
  name: EvidenceUebergangFeldName,
  wert: Wert,
): Werte => {
  const next: Werte = { ...prev, [name]: wert };
  aktion.felder.forEach((feld) => {
    if (feld.vorbelegt_aus !== name) return;
    if (!prev[feld.name] || prev[feld.name] === prev[name])
      next[feld.name] = wert;
  });
  return next;
};

const EvidenceUebergangDialog: React.FC<EvidenceUebergangDialogProps> = ({
  evidence,
  aktion: chosenAction,
  targetPath: presetPath,
  label: chosenLabel,
  onClose,
  onSubmit,
  notice,
  costItem,
  costAccess,
}) => {
  // Der erste Schritt aus „Offen“ kann den Weg wechseln – dann gilt der erste Schritt des
  // anderen Wegs, nicht mehr die gewählte Aktion.
  const [path, setPath] = useState<ProcessingPath | null>(null);
  const startOption = evidence?.path_options.find((o) => o.value === path);
  const isStartAction =
    !!chosenAction &&
    !!evidence?.path_options.some(
      (o) => o.start_action?.aktion === chosenAction.aktion,
    );
  const aktion =
    isStartAction && startOption?.start_action
      ? startOption.start_action
      : chosenAction;
  const label = aktion && aktion !== chosenAction ? aktion.label : chosenLabel;

  const [werte, setWerte] = useState<Werte>({});
  const [requester, setRequester] = useState<Requester>({
    side: "external",
    userId: "",
    institution: "",
    email: "",
  });
  const [targetPath, setTargetPath] = useState<ProcessingPath | "">("");
  const [amount, setAmount] = useState("");
  const [kommentar, setKommentar] = useState("");
  const [ziel, setZiel] = useState<EvidenceStatus | "">("");
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!evidence || !chosenAction) return;
    setPath(evidence.processing_path);
    setRequester(requesterOf(evidence));
    setTargetPath(presetPath ?? "");
    setAmount(initialAmount(chosenAction, costItem));
    setKommentar("");
    setZiel(chosenAction.ziel_optionen[0] ?? "");
    setFehler({});
    // Die Kostenposition nur beim Öffnen übernehmen – sonst setzte ein Neuladen der Positionen
    // den getippten Betrag zurück.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidence, chosenAction]);

  // Die Felder gehören zur Aktion – wechselt der Weg, wechseln sie mit.
  useEffect(() => {
    if (!evidence || !aktion) return;
    setWerte(startwerte(evidence, aktion));
  }, [evidence, aktion]);

  const zielOptionen = useMemo(
    () =>
      (aktion?.ziel_optionen ?? []).map((status) => ({
        value: status,
        label: status,
      })),
    [aktion],
  );

  const experts = useEvidenceExperts(
    !!aktion?.felder.some((feld) => feld.name === "gutachter"),
  );
  const asksRequester = !!aktion?.felder.some(
    (feld) => feld.typ === "requested_from",
  );
  const institutions = useEvidenceExperts(asksRequester, "institutions");
  const users = useUserOptions();
  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.name })),
    [users],
  );

  if (!evidence || !aktion) return null;

  const changesPath = aktion.aktion === "change_path";
  const pathOptions = evidence.path_options
    .filter((o) => !changesPath || o.value !== evidence.processing_path)
    .map((o) => ({
      value: o.value,
      label: o.label,
      tooltip: processingPathTooltip(o),
    }));
  // Den Kommentar verlangt der Server genau dann, wenn Fortschritt verloren geht.
  const losesProgress = changesPath && aktion.kommentar_pflicht;
  const targetPathLabel = evidence.path_options.find(
    (o) => o.value === targetPath,
  )?.label;

  const costField = costAccess !== "none" ? aktion.cost_field : null;
  const plannedForComparison =
    costField?.prefill_from != null
      ? costItem?.[costField.prefill_from]
      : undefined;

  const pruefen = (): boolean => {
    const gefunden: Record<string, string> = {};
    aktion.felder.forEach((feld) => {
      const leer =
        feld.typ === "requested_from"
          ? isRequesterEmpty(requester)
          : istLeer(werte[feld.name]);
      if (feld.pflicht && leer) {
        gefunden[feld.name] = "Pflichtfeld";
      }
    });
    if (changesPath && !targetPath) {
      gefunden.targetPath = "Pflichtfeld";
    }
    if (aktion.kommentar_pflicht && !kommentar.trim()) {
      gefunden.kommentar = "Pflichtfeld";
    }
    if (aktion.ziel_optionen.length > 0 && !ziel) {
      gefunden.ziel = "Pflichtfeld";
    }
    setFehler(gefunden);
    return Object.keys(gefunden).length === 0;
  };

  const handleSubmit = async () => {
    if (!pruefen()) return;
    setLaeuft(true);
    try {
      const newPath = changesPath
        ? targetPath || null
        : path !== evidence.processing_path
          ? path
          : null;
      const ok = await onSubmit({
        aktion: aktion.aktion,
        felder: Object.fromEntries(
          aktion.felder.map((feld) => [
            feld.name,
            feld.typ === "requested_from"
              ? requesterPayload(requester)
              : zumSenden(werte[feld.name]),
          ]),
        ),
        kommentar: kommentar.trim() || null,
        ziel_status: ziel || null,
        processing_path: newPath,
        ...(costField && costAccess === "write"
          ? { costs: { [costField.name]: alsBetrag(amount) } }
          : {}),
      });
      if (ok) onClose();
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <DialogTemplate
      open
      onClose={onClose}
      title={label}
      maxWidth="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={laeuft}>
            {label}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
        {notice && (
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            {notice}
          </Typography>
        )}
        {isStartAction && (
          <Dropdown
            label="Bearbeitungsweg"
            value={path ?? ""}
            options={pathOptions}
            onChange={(wert) => setPath(wert as ProcessingPath)}
            variant="floating"
            size="small"
          />
        )}
        {changesPath && (
          <>
            <Dropdown
              label="Neuer Bearbeitungsweg"
              value={targetPath}
              options={pathOptions}
              onChange={(wert) => {
                setTargetPath(wert as ProcessingPath);
                setFehler((prev) => ({ ...prev, targetPath: "" }));
              }}
              variant="floating"
              size="small"
              error={!!fehler.targetPath}
              helperText={fehler.targetPath}
            />
            {losesProgress && (
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                Der Nachweis steht bei „{evidence.status}“. Mit dem Wechsel
                {targetPathLabel ? ` zu „${targetPathLabel}“` : ""} beginnt er
                wieder bei „Offen“: Die bisherigen Schritte und ihre Termine
                entfallen. In der Nachweishistorie bleiben sie nachlesbar, und
                Dokumente, Notiz und Ansprechpartner bleiben am Nachweis.
              </Typography>
            )}
          </>
        )}
        {aktion.ziel_optionen.length > 0 && (
          <Dropdown
            label="Zurück auf"
            value={ziel}
            options={zielOptionen}
            onChange={(wert) => setZiel(wert as EvidenceStatus)}
            variant="floating"
            size="small"
            error={!!fehler.ziel}
            helperText={fehler.ziel}
          />
        )}

        {aktion.felder.map((feld) =>
          feld.typ === "dokument_liste" ? (
            // Dieselben Chips wie im Nachweis-Detail; gespeichert wird hier erst mit dem Knopf.
            <DokumentListe
              key={feld.name}
              label={feld.label}
              dokumente={(werte[feld.name] as Dokument[] | undefined) ?? []}
              onChange={(liste) => {
                setWerte((prev) => ({ ...prev, [feld.name]: liste }));
                setFehler((prev) => ({ ...prev, [feld.name]: "" }));
              }}
              required={feld.pflicht}
              error={fehler[feld.name]}
            />
          ) : feld.typ === "requested_from" ? (
            <Box
              key={feld.name}
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <Dropdown
                label={feld.label}
                value={requester.side}
                options={REQUESTER_SIDES}
                onChange={(wert) =>
                  setRequester((prev) => ({
                    ...prev,
                    side: wert as Requester["side"],
                  }))
                }
                variant="floating"
                size="small"
              />
              {requester.side === "internal" ? (
                <Dropdown
                  label="Kollegin oder Kollege"
                  value={requester.userId}
                  options={userOptions}
                  onChange={(wert) => {
                    setRequester((prev) => ({ ...prev, userId: wert }));
                    setFehler((prev) => ({ ...prev, [feld.name]: "" }));
                  }}
                  variant="floating"
                  size="small"
                  error={!!fehler[feld.name]}
                  helperText={fehler[feld.name]}
                />
              ) : (
                <>
                  <Autocomplete
                    label="Institution"
                    value={requester.institution}
                    options={institutions.map((i) => i.name)}
                    onChange={(wert) => {
                      setRequester((prev) => ({
                        ...prev,
                        institution: wert,
                        email: emailForName(
                          institutions,
                          prev.institution,
                          wert,
                          prev.email,
                        ),
                      }));
                      setFehler((prev) => ({ ...prev, [feld.name]: "" }));
                    }}
                    required={feld.pflicht}
                    error={fehler[feld.name]}
                    size="small"
                  />
                  <InputField
                    label="E-Mail"
                    value={requester.email}
                    onChange={(wert) =>
                      setRequester((prev) => ({ ...prev, email: wert }))
                    }
                    size="small"
                  />
                </>
              )}
            </Box>
          ) : feld.name === "gutachter" ? (
            <Autocomplete
              key={feld.name}
              label={feld.label}
              value={(werte[feld.name] as string | undefined) ?? ""}
              options={experts.map((e) => e.name)}
              onChange={(wert) =>
                setWerte((prev) => {
                  const next = mitFolgefeldern(aktion, prev, feld.name, wert);
                  if (aktion.felder.some((f) => f.name === "expert_email")) {
                    next.expert_email = emailForName(
                      experts,
                      (prev.gutachter as string | undefined) ?? "",
                      wert,
                      (prev.expert_email as string | undefined) ?? "",
                    );
                  }
                  return next;
                })
              }
              required={feld.pflicht}
              error={fehler[feld.name]}
              size="small"
            />
          ) : feld.typ === "notiz" ? (
            <InputFieldMultiline
              key={feld.name}
              label={feld.label}
              value={(werte[feld.name] as string | undefined) ?? ""}
              onChange={(wert) =>
                setWerte((prev) => ({ ...prev, [feld.name]: wert }))
              }
              required={feld.pflicht}
              error={fehler[feld.name]}
              placeholder="Was hakt, z. B. der Hersteller antwortet nicht"
              minRows={3}
            />
          ) : feld.typ === "text" || feld.typ === "email" ? (
            <InputField
              key={feld.name}
              label={feld.label}
              value={(werte[feld.name] as string | undefined) ?? ""}
              onChange={(wert) =>
                setWerte((prev) =>
                  mitFolgefeldern(aktion, prev, feld.name, wert),
                )
              }
              required={feld.pflicht}
              error={fehler[feld.name]}
              size="small"
            />
          ) : (
            <InputFieldDate
              key={feld.name}
              label={feld.label}
              value={(werte[feld.name] as string | undefined) ?? ""}
              onChange={(wert) =>
                setWerte((prev) =>
                  mitFolgefeldern(aktion, prev, feld.name, wert ?? ""),
                )
              }
              required={feld.pflicht}
              error={fehler[feld.name]}
              variant="floating"
              size="small"
              dateFormat="dd.MM.yyyy"
            />
          ),
        )}

        {costField && (
          <Box>
            <InputFieldNumber
              label={costField.label}
              value={amount}
              onChange={setAmount}
              readOnly={costAccess !== "write"}
              suffix="€"
              allowDecimals={false}
              variant="floating"
            />
            {plannedForComparison != null && (
              <Typography
                variant="caption"
                sx={{ color: colors.text.secondary, pl: 1.5 }}
              >
                Geplant: {formatCurrency(plannedForComparison)}
              </Typography>
            )}
          </Box>
        )}

        <InputFieldMultiline
          label={
            aktion.kommentar_pflicht ? "Kommentar" : "Kommentar (optional)"
          }
          value={kommentar}
          onChange={setKommentar}
          required={aktion.kommentar_pflicht}
          error={fehler.kommentar}
          minRows={3}
        />
      </Box>
    </DialogTemplate>
  );
};

export default EvidenceUebergangDialog;
